/**
 * Simulation Presets
 * 
 * Predefined simulation configurations for common stress-testing scenarios.
 * These presets cover typical production patterns and edge cases.
 */

import {
  createDefaultLeadDistribution,
  createDefaultAgentPopulation,
  createDefaultContractMix,
} from './syntheticDataGenerator';
import { stringToSeed } from './seededRandom';
import type { SimulationConfig } from './types';

// --- Time Helpers ---

const DAY_MS = 24 * 60 * 60 * 1000;

function daysAgo(days: number): number {
  const now = Date.now();
  return now - (days * DAY_MS);
}

// --- Presets ---

export type SimulationPresetName = 
  | 'standard_30_day'
  | 'high_volume_stress'
  | 'contract_heavy'
  | 'high_cost_states'
  | 'oversold_inventory'
  | 'agent_scaling'
  | 'retry_stress'
  | 'quick_smoke_test';

export interface SimulationPreset {
  name: SimulationPresetName;
  displayName: string;
  description: string;
  estimatedDuration: string;
  config: Omit<SimulationConfig, 'runId' | 'progressCallback' | 'progressInterval'>;
}

/**
 * Standard 30-day simulation
 * 100 agents, 5 contracts, ~1000 leads
 */
export const STANDARD_30_DAY: SimulationPreset = {
  name: 'standard_30_day',
  displayName: '30 Days / 100 Agents / 5 Contracts',
  description: 'Standard production scenario simulating one month of typical routing activity',
  estimatedDuration: '~10 seconds',
  config: {
    seed: stringToSeed('standard_30_day_v1'),
    numberOfLeads: 1000,
    startDate: daysAgo(30),
    endDate: Date.now(),
    leadDistribution: createDefaultLeadDistribution(),
    agentPopulation: createDefaultAgentPopulation(100),
    contractMix: createDefaultContractMix(),
  },
};

/**
 * High-volume stress test
 * 200 agents, 10 contracts, 5000 leads in 14 days
 */
export const HIGH_VOLUME_STRESS: SimulationPreset = {
  name: 'high_volume_stress',
  displayName: 'High Volume Stress Test',
  description: '5000 leads through 200 agents in 14 days - tests system under heavy load',
  estimatedDuration: '~30-60 seconds',
  config: {
    seed: stringToSeed('high_volume_stress_v1'),
    numberOfLeads: 5000,
    startDate: daysAgo(14),
    endDate: Date.now(),
    leadDistribution: createDefaultLeadDistribution(),
    agentPopulation: {
      ...createDefaultAgentPopulation(200),
      monthlyTargetRange: { min: 20, max: 80 },
      guaranteedMinimumPct: 40,
    },
    contractMix: {
      monthlyTargetCount: 6,
      prepaidPackageCount: 3,
      trialCount: 1,
      leadsCommittedRange: { min: 50, max: 200 },
      coverageRatio: 0.4,
    },
  },
};

/**
 * Contract-heavy scenario
 * Many contracts relative to lead volume - tests delivery pressure
 */
export const CONTRACT_HEAVY: SimulationPreset = {
  name: 'contract_heavy',
  displayName: 'Contract-Heavy Pressure',
  description: 'Many contracts competing for limited leads - tests pacing and pressure enforcement',
  estimatedDuration: '~15 seconds',
  config: {
    seed: stringToSeed('contract_heavy_v1'),
    numberOfLeads: 800,
    startDate: daysAgo(21),
    endDate: Date.now(),
    leadDistribution: createDefaultLeadDistribution(),
    agentPopulation: createDefaultAgentPopulation(80),
    contractMix: {
      monthlyTargetCount: 10,
      prepaidPackageCount: 5,
      trialCount: 3,
      leadsCommittedRange: { min: 30, max: 80 },
      coverageRatio: 0.6, // Contracts want 60% of leads
    },
  },
};

/**
 * High-cost states dominant
 * Leads concentrated in expensive states like CA, NY, FL
 */
export const HIGH_COST_STATES: SimulationPreset = {
  name: 'high_cost_states',
  displayName: 'High-Cost States Dominant',
  description: 'Lead distribution skewed to high-value states - tests pricing and licensing dynamics',
  estimatedDuration: '~10 seconds',
  config: {
    seed: stringToSeed('high_cost_states_v1'),
    numberOfLeads: 1200,
    startDate: daysAgo(30),
    endDate: Date.now(),
    leadDistribution: {
      stateWeights: {
        CA: 30, NY: 25, FL: 20, TX: 15, IL: 10,
      },
      leadTypeWeights: {
        mortgage_purchase: 30,
      },
      verticalWeights: {
        life_insurance: 70,
        mortgage: 30,
      },
      valueRange: {
        min: 30,
        max: 100,
        mean: 55,
        stdDev: 20,
      },
    },
    agentPopulation: {
      ...createDefaultAgentPopulation(100),
      licensedStatesRange: { min: 3, max: 15 }, // Fewer licensed states
    },
    contractMix: createDefaultContractMix(),
  },
};

/**
 * Oversold inventory scenario
 * Total contract commitments exceed available leads
 */
export const OVERSOLD_INVENTORY: SimulationPreset = {
  name: 'oversold_inventory',
  displayName: 'Oversold Inventory',
  description: 'Contract commitments exceed lead supply - tests fairness and breach handling',
  estimatedDuration: '~15 seconds',
  config: {
    seed: stringToSeed('oversold_inventory_v1'),
    numberOfLeads: 500,
    startDate: daysAgo(30),
    endDate: Date.now(),
    leadDistribution: createDefaultLeadDistribution(),
    agentPopulation: createDefaultAgentPopulation(50),
    contractMix: {
      monthlyTargetCount: 8,
      prepaidPackageCount: 4,
      trialCount: 2,
      leadsCommittedRange: { min: 40, max: 100 },
      coverageRatio: 1.5, // 150% of leads promised (impossible to fulfill all)
    },
  },
};

/**
 * Agent scaling test
 * Many agents, few leads - tests pool selection at scale
 */
export const AGENT_SCALING: SimulationPreset = {
  name: 'agent_scaling',
  displayName: 'Large Agent Pool Scaling',
  description: '500 agents competing for leads - tests pool selection performance',
  estimatedDuration: '~45 seconds',
  config: {
    seed: stringToSeed('agent_scaling_v1'),
    numberOfLeads: 2000,
    startDate: daysAgo(14),
    endDate: Date.now(),
    leadDistribution: createDefaultLeadDistribution(),
    agentPopulation: createDefaultAgentPopulation(500),
    contractMix: {
      monthlyTargetCount: 5,
      prepaidPackageCount: 2,
      trialCount: 1,
      leadsCommittedRange: { min: 50, max: 150 },
      coverageRatio: 0.25,
    },
  },
};

/**
 * Retry stress test
 * Scenario designed to trigger many retries
 */
export const RETRY_STRESS: SimulationPreset = {
  name: 'retry_stress',
  displayName: 'Retry Queue Stress',
  description: 'Conditions that trigger many retries - tests retry logic and stock behavior',
  estimatedDuration: '~20 seconds',
  config: {
    seed: stringToSeed('retry_stress_v1'),
    numberOfLeads: 1500,
    startDate: daysAgo(7),
    endDate: Date.now(),
    leadDistribution: {
      stateWeights: {
        // Concentrate on states with few licensed agents
        MT: 20, WY: 20, ND: 20, SD: 20, VT: 20,
      },
      leadTypeWeights: {
        life_fex: 50, // Less common lead type
        mortgage_heloc: 50,
      },
      verticalWeights: {
        life_insurance: 50,
        mortgage: 50,
      },
      valueRange: {
        min: 10,
        max: 25, // Lower values = fewer eligible agents
        mean: 18,
        stdDev: 5,
      },
    },
    agentPopulation: {
      ...createDefaultAgentPopulation(50),
      licensedStatesRange: { min: 2, max: 8 },
      walletBalanceRange: { min: 100, max: 500 }, // Lower balances
    },
    contractMix: createDefaultContractMix(),
  },
};

/**
 * Quick smoke test
 * Fast validation that everything works
 */
export const QUICK_SMOKE_TEST: SimulationPreset = {
  name: 'quick_smoke_test',
  displayName: 'Quick Smoke Test',
  description: 'Fast 100-lead test to verify simulation engine works correctly',
  estimatedDuration: '~2 seconds',
  config: {
    seed: stringToSeed('quick_smoke_test_v1'),
    numberOfLeads: 100,
    startDate: daysAgo(7),
    endDate: Date.now(),
    leadDistribution: createDefaultLeadDistribution(),
    agentPopulation: createDefaultAgentPopulation(20),
    contractMix: {
      monthlyTargetCount: 2,
      prepaidPackageCount: 1,
      trialCount: 0,
      leadsCommittedRange: { min: 10, max: 30 },
      coverageRatio: 0.3,
    },
  },
};

// --- All Presets Map ---

export const SIMULATION_PRESETS: Record<SimulationPresetName, SimulationPreset> = {
  standard_30_day: STANDARD_30_DAY,
  high_volume_stress: HIGH_VOLUME_STRESS,
  contract_heavy: CONTRACT_HEAVY,
  high_cost_states: HIGH_COST_STATES,
  oversold_inventory: OVERSOLD_INVENTORY,
  agent_scaling: AGENT_SCALING,
  retry_stress: RETRY_STRESS,
  quick_smoke_test: QUICK_SMOKE_TEST,
};

export const ALL_PRESETS = Object.values(SIMULATION_PRESETS);

/**
 * Create a full simulation config from a preset
 */
export function createConfigFromPreset(
  preset: SimulationPreset,
  overrides?: Partial<SimulationConfig>
): SimulationConfig {
  return {
    runId: `${preset.name}_${Date.now()}`,
    ...preset.config,
    ...overrides,
  };
}
